import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  AlertTriangle,
  Clock,
  CheckCircle2,
  ShieldAlert,
  ArrowRight,
  Search
} from 'lucide-react';
import { useGst } from '../../context/GstContext';
import {
  calculateStatutoryDeadlines,
  SAMPLE_CLIENT_PORTFOLIO
} from '../../services/practiceManagementService';
import { ClientPortfolioSummary } from '../../types/gst';
import { StatutoryDeadlinesTicker } from '../../components/practice/StatutoryDeadlinesTicker';
import { toast } from '../../components/ui';

export const PracticeManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isSuperOrCa, setClientId, financialYear, taxPeriod } = useGst();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'ACTION_REQUIRED' | 'FILED'>('ALL');
  const [portfolioList] = useState<ClientPortfolioSummary[]>(SAMPLE_CLIENT_PORTFOLIO);

  // Security guard check
  const isAdmin = isSuperOrCa || user?.email === 'admin@verveailabs.com';

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center space-y-4 pt-20">
        <div className="h-16 w-16 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 mx-auto flex items-center justify-center">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-white">Access Restricted</h2>
        <p className="text-xs text-slate-400 leading-relaxed">
          The CA Practice Management Portfolio is strictly reserved for authorized administrators at{' '}
          <span className="text-cyan-400 font-mono">admin@verveailabs.com</span>. Please contact support if you require CA firm multi-client delegation.
        </p>
        <button
          onClick={() => navigate('/vault')}
          className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition"
        >
          Return to Client Vault
        </button>
      </div>
    );
  }

  const deadlines = calculateStatutoryDeadlines();

  const filteredPortfolio = portfolioList.filter(client => {
    const matchesSearch =
      client.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.gstin.toLowerCase().includes(searchTerm.toLowerCase());

    const isActionRequired = client.gstr3bStatus !== 'FILED' || client.unreconciledCount > 0;
    if (filterStatus === 'ACTION_REQUIRED') return matchesSearch && isActionRequired;
    if (filterStatus === 'FILED') return matchesSearch && client.gstr3bStatus === 'FILED';
    return matchesSearch;
  });

  const totalClients = portfolioList.length;
  const overdueClients = portfolioList.filter(c => c.daysUntilDeadline < 0).length;
  const totalTaxAtRisk = portfolioList.reduce((acc, c) => acc + c.taxAtRisk, 0);

  const handleSwitchClient = (targetClientId: string, clientName: string) => {
    setClientId(targetClientId);
    toast.success(`Switched active workspace to ${clientName}`);
    navigate('/returns');
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Page Title & Super Admin Badge */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-cyan-400" />
              <span>CA Multi-Client Practice Management Portfolio</span>
            </h1>
            <span className="rounded-full border border-cyan-500/40 bg-cyan-950/60 text-cyan-300 font-mono text-[10px] font-bold px-2.5 py-0.5">
              Super Admin / CA Command Center
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Multi-client compliance command center: Track statutory return deadlines, overdue filings, late fee risk & switch client contexts
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#131316] px-4 py-1.5 text-xs text-slate-300">
          Statutory Period: <strong className="text-white font-mono">{taxPeriod} {financialYear}</strong>
        </div>
      </div>

      {/* Deadlines Countdown Ticker */}
      <StatutoryDeadlinesTicker deadlines={deadlines} />

      {/* KPI Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#131316] p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-slate-400">Total Clients Under CA</div>
          <div className="text-xl font-bold text-white font-mono">{totalClients}</div>
          <div className="text-[10px] text-slate-500">Organizations in portfolio</div>
        </div>

        <div className="rounded-2xl border border-rose-500/25 bg-rose-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-rose-400 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>Overdue Filings</span>
          </div>
          <div className="text-xl font-bold text-rose-300 font-mono">{overdueClients}</div>
          <div className="text-[10px] text-rose-400/70">Late fees accumulating daily</div>
        </div>

        <div className="rounded-2xl border border-amber-500/25 bg-amber-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-amber-400 flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>Total ITC at Risk</span>
          </div>
          <div className="text-xl font-bold text-amber-300 font-mono">
            ₹{totalTaxAtRisk.toLocaleString('en-IN')}
          </div>
          <div className="text-[10px] text-amber-400/70">Unreconciled purchase invoices</div>
        </div>

        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-950/20 p-4 text-xs space-y-1">
          <div className="text-[10px] uppercase font-bold text-emerald-400 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Filing Success Rate</span>
          </div>
          <div className="text-xl font-bold text-emerald-300 font-mono">
            {Math.round((portfolioList.filter(c => c.gstr1Status === 'FILED').length / totalClients) * 100)}%
          </div>
          <div className="text-[10px] text-emerald-400/70">GSTR-1 ARN compliance rate</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#131316] p-3 rounded-2xl border border-white/10 text-xs">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by client name, GSTIN..."
            className="w-full rounded-xl border border-white/10 bg-[#0e1015] pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
        </div>

        <div className="flex items-center gap-1.5">
          {[
            { key: 'ALL', label: 'All Clients' },
            { key: 'ACTION_REQUIRED', label: 'Action Required' },
            { key: 'FILED', label: 'Completed' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key as any)}
              className={`px-3 py-1.5 rounded-xl font-medium transition ${
                filterStatus === tab.key
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Multi-Client Portfolio Grid */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131316]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0e1015] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Client Organization</th>
              <th className="py-3 px-4">Frequency</th>
              <th className="py-3 px-4 text-center">GSTR-1</th>
              <th className="py-3 px-4 text-center">GSTR-3B</th>
              <th className="py-3 px-4 text-center">2B Recon</th>
              <th className="py-3 px-4 text-right">ITC at Risk</th>
              <th className="py-3 px-4 text-right">Late Fee Exposure</th>
              <th className="py-3 px-4 text-right">CA Switch</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300">
            {filteredPortfolio.map(c => (
              <tr key={c.clientId} className="hover:bg-white/[0.02] transition">
                <td className="py-3 px-4 font-sans">
                  <div className="font-bold text-white">{c.legalName}</div>
                  <div className="font-mono text-[10px] text-slate-400">{c.gstin} &bull; {c.stateName}</div>
                </td>

                <td className="py-3 px-4 font-sans">
                  <span className="rounded bg-white/5 px-2 py-0.5 text-[10px] text-slate-300">
                    {c.filingFrequency}
                  </span>
                </td>

                <td className="py-3 px-4 text-center font-sans">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                      c.gstr1Status === 'FILED'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : c.gstr1Status === 'READY'
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                    }`}
                  >
                    {c.gstr1Status}
                  </span>
                </td>

                <td className="py-3 px-4 text-center font-sans">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                      c.gstr3bStatus === 'FILED'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : c.gstr3bStatus === 'READY'
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}
                  >
                    {c.gstr3bStatus}
                  </span>
                </td>

                <td className="py-3 px-4 text-center font-sans">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                      c.gstr2bReconStatus === 'COMPLETED'
                        ? 'text-emerald-400'
                        : c.gstr2bReconStatus === 'IN_PROGRESS'
                        ? 'text-amber-400'
                        : 'text-slate-500'
                    }`}
                  >
                    {c.gstr2bReconStatus}
                  </span>
                </td>

                <td className="py-3 px-4 text-right font-bold text-white">
                  {c.taxAtRisk > 0 ? (
                    <span className="text-amber-400">₹{c.taxAtRisk.toLocaleString('en-IN')}</span>
                  ) : (
                    <span className="text-emerald-400 font-normal">₹0.00</span>
                  )}
                </td>

                <td className="py-3 px-4 text-right">
                  {c.estimatedLateFeeSec47 > 0 ? (
                    <span className="text-rose-400 font-bold">
                      ₹{c.estimatedLateFeeSec47} (Sec 47)
                    </span>
                  ) : (
                    <span className="text-slate-500">₹0</span>
                  )}
                </td>

                <td className="py-3 px-4 text-right font-sans">
                  <button
                    onClick={() => handleSwitchClient(c.clientId, c.legalName)}
                    className="flex items-center gap-1 ml-auto px-2.5 py-1 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 text-xs font-semibold border border-cyan-500/30 transition shadow-sm"
                  >
                    <span>Open Client</span>
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
